/**
 * Somebody else's JSON, rendered as something a business owner can read.
 *
 * `outcome.fields` is the one place the platform stops caring what the shape is (see the
 * note on it in `wire.ts`). A boat shop fills it with hulls and battery chemistry; a
 * dentist fills it with chairs and recall dates. This file renders both, and it contains
 * no word from either vertical.
 *
 * IT DISPATCHES ON SHAPE, NOT ON A LIST OF KNOWN KEYS. That is the whole design. A
 * per-agent field configuration was the obvious alternative and it is worse in the way
 * that matters: an agent that ships a new field on Tuesday would see it rendered as a raw
 * key until somebody edited a config file, so the common case - a field nobody has
 * thought about yet - is exactly the case it handles badly. Here a new field is just
 * another scalar, and it lands in the right place on Tuesday.
 *
 * The rules, in the order they are tried:
 *
 *   scalar                 a label/value row
 *   boolean                a tick or a dash, never the strings "true" and "false"
 *   number with a unit key `lengthFt: 34` reads "Length · 34 ft" - see `unit()`
 *   object                 its own sub-section under its own heading
 *   array of scalars       chips when they are words, a list when they are sentences
 *   array of like objects  a small table
 *   anything deeper        one compact line, which is honest: nobody has said how it
 *                          should look, and inventing a layout for it would be guessing
 *
 * Order is scalars, then objects, then arrays - so the one-line facts are on top and the
 * long lists are at the bottom, without anybody configuring anything.
 */

import { createContext, useContext, type ReactNode } from "react";
import { Chip, Panel, TABLE, TD, TH, cx } from "../ui";
import { humanise, label, money, round, spaced, unit } from "../format";

/** Deeper than this and the shape is not something a label/value table can carry. */
const MAX_DEPTH = 3;

/** A list longer than this is a data dump, and it gets a lid. */
const LONG_LIST = 12;

/**
 * The currency a bare `…Minor` key is money IN.
 *
 * A CONTEXT RATHER THAN A PROP THREADED THROUGH FIVE COMPONENTS. It is read in exactly one
 * place - `asMoney`, at the bottom of a recursion three levels deep - and drilling it
 * through `Rows`, `Field`, `Value`, `ListOf` and `ObjectTable` would put a parameter that
 * four of them do not use into all five signatures.
 *
 * It matters because the current agent decides it now. `priceMinor: 234600` in a boat shop's
 * outcome bag is dollars and in a London practice's is pounds, and the field itself never
 * said which - it used to render as AUD for everybody, which was right for every organization
 * there was and would be silently wrong for the first one there is not.
 */
const Currency = createContext<string | null>(null);

export function Fields({
  fields,
  currency,
  bare,
}: {
  fields: Record<string, unknown>;
  /** The current agent's currency. Null falls back to `money()`'s own default. */
  currency?: string | null;
  /** Drop the card and the heading: the caller has already drawn a section around this. */
  bare?: boolean;
}) {
  const entries = Object.entries(fields).filter(([, value]) => hasContent(value));
  if (!entries.length) return null;

  const { scalars, objects, arrays } = group(entries);
  // The `@container` stays on both branches. It is what every `@min-*` below measures
  // against, and dropping it in the bare case would silently give a 440px column the
  // four-track layout - the exact bug the header comment is about.
  const Wrapper = bare ? BareBox : Panel;

  return (
    <Currency.Provider value={currency ?? null}>
    <Wrapper title="Structured details" className="@container">
      {scalars.length > 0 && <Rows entries={scalars} depth={0} />}

      {objects.map(([key, value]) => (
        <Section key={key} title={label(key)}>
          <Rows entries={Object.entries(value as Record<string, unknown>).filter(([, v]) => hasContent(v))} depth={1} />
        </Section>
      ))}

      {arrays.map(([key, value]) => (
        <Section key={key} title={label(key)}>
          <ListOf items={value as unknown[]} depth={1} />
        </Section>
      ))}
    </Wrapper>
    </Currency.Provider>
  );
}

/** `Panel`'s shape with none of `Panel`'s chrome, so the two are interchangeable above. */
function BareBox({ className, children }: { title?: string; className?: string; children: ReactNode }) {
  return <div className={className}>{children}</div>;
}

/**
 * Scalars first, then objects, then arrays.
 *
 * Within each group the agent's own insertion order is kept, because the agent author
 * wrote the object in the order they think about the call and that is better information
 * than anything this file could infer.
 */
function group(entries: Array<[string, unknown]>) {
  const scalars: Array<[string, unknown]> = [];
  const objects: Array<[string, unknown]> = [];
  const arrays: Array<[string, unknown]> = [];
  for (const entry of entries) {
    const value = entry[1];
    if (Array.isArray(value)) arrays.push(entry);
    else if (value !== null && typeof value === "object") objects.push(entry);
    else scalars.push(entry);
  }
  return { scalars, objects, arrays };
}

/** A heading only earns a rule above it when something came before it. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-4 border-t border-line-soft pt-3.5 first:mt-0 first:border-t-0 first:pt-0">
      <h3 className="mb-2.5 text-2xs font-semibold tracking-[0.06em] text-ink-soft uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The facts of one section, flowed into as many columns as the card is wide enough for.
 *
 * A SINGLE COLUMN OF SHORT PAIRS WASTES MOST OF THE CARD. "Length · 34 ft" is nine
 * characters of content in a wide card, and stacking a dozen of them leaves a gutter
 * wider than the data with a scroll to match. Two label/value pairs per row halves the
 * height and reads the same, because each pair keeps its own label column - the eye
 * still lands on a label and moves right to its value.
 *
 * IT IS A CONTAINER QUERY, NOT A VIEWPORT ONE, and that distinction is load-bearing now
 * that this panel is the narrow half of a two-column row rather than the width of the
 * page. Keyed off the window, a wide monitor turned on the four-track grid inside a 330px
 * column and broke "graded" into six stacked letters. The card asks how wide the CARD is.
 *
 * The exception is the long value. `chargingSummary` is a sentence and `breakdown` is a
 * costing line; squeezed into a half-width column they wrap three times and the tidy
 * grid turns into ragged prose. Anything past `WIDE` takes the full row instead, so the
 * layout follows the content rather than the content fighting the layout.
 */
const WIDE = 44;

function Rows({ entries, depth }: { entries: Array<[string, unknown]>; depth: number }) {
  if (!entries.length) return null;
  return (
    <dl
      className={cx(
        "grid items-baseline gap-x-4 gap-y-[9px] text-sm",
        "grid-cols-[minmax(88px,max-content)_minmax(0,1fr)]",
        "@min-[560px]:grid-cols-[repeat(2,minmax(88px,max-content)_minmax(0,1fr))] @min-[560px]:gap-x-7",
      )}
    >
      {entries.map(([key, value]) => (
        <Field key={key} label={label(key)} wide={isWide(value)}>
          <Value value={value} unitFor={key} depth={depth} />
        </Field>
      ))}
    </dl>
  );
}

/** A pair that can claim the whole row. `ui.tsx`'s `Pair` cannot, and should not learn to. */
function Field({
  label: text,
  wide,
  children,
}: {
  label: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <dt className={cx("text-sm text-ink-soft", wide && "@min-[560px]:col-start-1")}>{text}</dt>
      <dd
        className={cx(
          "m-0 text-ink [overflow-wrap:anywhere]",
          "[&_a]:text-accent hover:[&_a]:underline",
          wide && "@min-[560px]:col-span-3",
        )}
      >
        {children}
      </dd>
    </>
  );
}

/**
 * Is this value too long to sit in half a card?
 *
 * Measured on the value rather than declared per field, because the platform does not
 * know which of an agent's keys hold sentences - and the same key can hold "yes" on one
 * call and two lines of reasoning on the next.
 */
function isWide(value: unknown): boolean {
  if (typeof value === "string") return value.length > WIDE;
  if (value !== null && typeof value === "object") return true;
  return false;
}

/**
 * One value.
 *
 * THE VALUE IS FULL INK AND THE LABEL IS THE QUIET ONE. This panel used to set both a
 * step down - the theory being that an agent's raw field is not a fact the platform stands
 * behind - and the result was a whole section of grey on grey that read as disabled. The
 * ordering it wanted is still here and it is the same one the transcript's action cards
 * use: the label is scaffolding, the value is what somebody came to read, so the value is
 * the darker of the two.
 */
function Value({ value, unitFor, depth }: { value: unknown; unitFor?: string; depth: number }) {
  const currency = useContext(Currency);

  if (typeof value === "boolean") {
    // Not "yes"/"no". A column of the word "no" reads as a column of failures, and half
    // of these booleans are neutral facts about a boat.
    return value ? (
      <span className="text-good">Yes</span>
    ) : (
      <span className="text-ink-soft">No</span>
    );
  }

  if (typeof value === "number") {
    const suffix = unitFor ? unit(unitFor)?.suffix : undefined;
    const shown = asMoney(unitFor, value, currency) ?? `${round(value)}${suffix ? spaced(suffix) : ""}`;
    return <span className="nums">{shown}</span>;
  }

  if (typeof value === "string") return <>{humanise(value)}</>;

  if (Array.isArray(value)) return <ListOf items={value} depth={depth + 1} />;

  if (value !== null && typeof value === "object") {
    if (depth + 1 > MAX_DEPTH) return <>{compact(value)}</>;
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => hasContent(v));
    if (!entries.length) return null;
    return (
      <div className="grid gap-[3px]">
        {entries.map(([key, child]) => (
          <span key={key}>
            <span className="text-ink-soft">{label(key)} </span>
            <Value value={child} unitFor={key} depth={depth + 1} />
          </span>
        ))}
      </div>
    );
  }

  return <>—</>;
}

/**
 * A list, rendered as what its contents actually are.
 *
 * Three words are chips; three sentences are a list, because chips that wrap onto their
 * own lines are just a list with borders on it. `gradeReasons` is the case that decides
 * this - they are whole sentences and they are the most useful thing in the bag.
 */
function ListOf({ items, depth }: { items: unknown[]; depth: number }) {
  const kept = items.filter(hasContent);
  if (!kept.length) return null;

  const objects = kept.every((item) => item !== null && typeof item === "object" && !Array.isArray(item));
  if (objects && depth <= MAX_DEPTH) {
    return <ObjectTable items={kept as Array<Record<string, unknown>>} depth={depth} />;
  }

  // Words become chips; sentences become a list. Chips that each wrap onto their own
  // line are a list with borders on it, which is strictly worse than a list.
  const words = kept.filter((item): item is string => typeof item === "string" && item.length <= 28);
  if (words.length === kept.length) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {words.map((word, i) => (
          <Chip key={`${word}-${i}`}>{humanise(word)}</Chip>
        ))}
      </div>
    );
  }

  // BULLETS, NOT THE RULED BLOCKS `CallReading` draws for unanswered questions. Those are
  // sentences about the call, one to a block; this is an agent's bag of products or tags,
  // and ruling each one reads as a series of statements somebody made.
  const body = (
    <ul className="grid list-disc gap-2 pl-4.5 text-sm leading-normal text-ink marker:text-ink-faint">
      {kept.map((item, i) => (
        <li key={i}>{typeof item === "string" ? item : compact(item)}</li>
      ))}
    </ul>
  );

  return kept.length > LONG_LIST ? <Lid count={kept.length}>{body}</Lid> : body;
}

/**
 * A list of objects: a table when they are narrow and alike, blocks when they are not.
 *
 * NEITHER BRANCH IS ALLOWED TO DROP A KEY. The first version of this had one cap - six
 * columns - and everything above it fell through to the compact one-liner, which slices
 * to six entries. A product here carries ten keys, so the page rendered "ID · Brand ·
 * Model · Drive · Power · Price" and silently swallowed the URL, the notes and the
 * quantity. A field that vanishes without saying so is the one failure this whole file
 * is written against, and a cap that turns extra data into missing data is that failure
 * with a threshold on it.
 *
 * So the cap now chooses a LAYOUT rather than deciding what survives: few enough columns
 * to scan across, and it is a table; more than that, and each item becomes its own small
 * block with every key in it.
 */
const TABLE_COLUMNS = 5;

function ObjectTable({ items, depth }: { items: Array<Record<string, unknown>>; depth: number }) {
  const keys = [...new Set(items.flatMap((item) => Object.keys(item)))].filter((key) =>
    items.some((item) => hasContent(item[key])),
  );
  if (!keys.length) return null;

  // A union of keys across ragged rows makes a table of mostly empty cells, which is
  // worse than the blocks below - so the table needs them all to carry the same keys.
  const alike = items.every((item) => keys.every((key) => key in item));

  if (alike && keys.length <= TABLE_COLUMNS) {
    const table = (
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead>
            <tr>
              {keys.map((key) => (
                <th key={key} className={TH}>
                  {label(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                {keys.map((key) => (
                  <td key={key} className={TD}>
                    <Value value={item[key]} unitFor={key} depth={depth + 1} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    return items.length > LONG_LIST ? <Lid count={items.length}>{table}</Lid> : table;
  }

  const blocks = (
    <div className="grid gap-3.5">
      {items.map((item, i) => (
        <div key={i} className="border-t border-line-soft pt-3 first:border-t-0 first:pt-0">
          <Rows
            entries={Object.entries(item).filter(([, value]) => hasContent(value))}
            depth={depth + 1}
          />
        </div>
      ))}
    </div>
  );
  return items.length > LONG_LIST ? <Lid count={items.length}>{blocks}</Lid> : blocks;
}

/** Anything past a dozen rows opens on request rather than on arrival. */
function Lid({ count, children }: { count: number; children: ReactNode }) {
  return (
    <details className="disclosure">
      <summary className="flex cursor-pointer items-center gap-2 text-xs font-medium text-ink-soft hover:text-ink">
        Show all {count}
      </summary>
      <div className="mt-2.5">{children}</div>
    </details>
  );
}

/**
 * An empty list is not a fact worth a row.
 *
 * `false` and `0` are: "safety incident: no" is something an owner may specifically want
 * to see, whereas "objections:" followed by nothing reads as a value that failed to load.
 * Recursive, so an object whose every value is null is empty too - which is the shape a
 * call that ended early produces, and the reason the page has anything to hide.
 */
export function hasContent(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.some(hasContent);
  if (typeof value === "object") return Object.values(value as object).some(hasContent);
  return true;
}

/**
 * Cents, but only where the key says so. A bare number is not money.
 *
 * The currency comes from the current agent rather than from a default in this file: the same
 * `priceMinor` is dollars in Sydney and pounds in London, and the key never said which.
 */
function asMoney(key: string | undefined, value: number, currency: string | null): string | null {
  if (!key) return null;
  return /(?:Minor|Cents|_minor|_cents)$/.test(key) ? money(value, currency) : null;
}

function compact(value: unknown): string {
  if (!value || typeof value !== "object") return String(value);
  return Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => hasContent(v) && typeof v !== "object")
    .slice(0, 6)
    .map(([k, v]) => `${label(k)}: ${typeof v === "boolean" ? (v ? "yes" : "no") : v}`)
    .join(" · ");
}
